import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { MaterialUtilitiesService } from '../../services/material-utilities/material-utilities.service';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { UserGidService } from '../../services/user-gid/user-gid.service';
import { UserMasterService } from '../../services/user-master/user-master.service';
import { AddressModel } from '../complex/address.model';
import { ArrayModel } from '../complex/array.model';
import { CompositeModel } from '../complex/composite.model';
import { CseDopModel, ODataCseDopOutModel } from '../complex/cse-dop.model';
import { DropdownModel } from '../complex/dropdown.model';
import { RadioModel } from '../complex/radio.model';
import { MaterialPartSetModel, ODataMaterialPartSetOutModel } from '../low-level/material-part-set.model';
import { MATERIAL_SOURCE_CODE } from '../low-level/material-source.model';
import { MATERIAL_STATUS_CODE } from '../low-level/material-status.model';
import { MaterialModel } from '../low-level/material.model';
import {
    NotificationIdentifierModel,
    ODataNotificationIdentifierOutModel,
} from '../low-level/notification-identifier.model';
import { RadioModelOptions } from '../options/radio-model-options';
import { BooleanModel } from '../primitive/boolean.model';
import { TextModel } from '../primitive/text.model';

export class MaterialCentralReturnModel extends CompositeModel {
    protected clientManager: ClientManagerInterface;
    public material: MaterialModel;
    public notification: NotificationIdentifierModel;
    public materialReturnPartSet: ArrayModel<MaterialPartSetModel>;
    public dopAddress: CseDopModel;
    public selectedPartSet: MaterialPartSetModel;

    public deliveryReturnOptions: RadioModel<TextModel>;
    public dropOffPointOptions: DropdownModel<CseDopModel>;
    public noPhysicalReturn: DisplayDecorator<BooleanModel>;
    public returnInfo: TextModel;
    public awbPackaging: TextModel;
    public readonly KEY_TAXI = 'TAXI';
    public readonly KEY_FEDEX = 'FEDEX';
    public customerAddress: CseDopModel;
    private date: TextModel;
    private time: TextModel;

    public constructor(
        materialModel: MaterialModel,
        public notificationIdentifierModel: NotificationIdentifierModel,
        public cseDopList: Array<DisplayDecorator<CseDopModel>>,
        public materialUtilities: MaterialUtilitiesService,
    ) {
        super('MaterialCentralReturnModel');

        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);
        const configService = injector.get(ConfigService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        this.clientManager = injector.get(ClientManagerInstanceToken);

        this.material = materialModel;
        this.notification = <NotificationIdentifierModel>this.set(notificationIdentifierModel);
        this.date = <TextModel>this.set(new TextModel('Date', moment().format(ConfigConstants.formatDateNoTimezone)));
        this.time = <TextModel>this.set(new TextModel('Time', moment().format(ConfigConstants.formatXSDTimeDuration)));

        const addDeliveryAddressTaxi = configService.getAddDeliveryAddressTaxi();

        // build the radio button option for address
        const deliveryOptions: Array<DisplayDecorator<TextModel>> = [];
        deliveryOptions.push(
            new DisplayDecorator(
                <string>translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_CUSTOMER'),
                new TextModel(ConfigConstants.returnCustomer),
            ),
        );
        deliveryOptions.push(
            new DisplayDecorator(
                <string>translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_DOP'),
                new TextModel(ConfigConstants.returnDop),
            ),
        );
        deliveryOptions.push(
            new DisplayDecorator(
                <string>translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_FEDEX'),
                new TextModel(ConfigConstants.returnFedex),
            ),
        );
        if (addDeliveryAddressTaxi) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_TAXI'),
                    new TextModel(ConfigConstants.returnTaxi),
                ),
            );
        }
        if (this.clientManager.isNgmP58()) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_N/A'),
                    new TextModel(ConfigConstants.returnNotSelected),
                ),
            );
        }
        this.deliveryReturnOptions = new RadioModel<TextModel>(
            'DeliveryAddress',
            deliveryOptions,
            new RadioModelOptions({
                initialSelection: this.materialUtilities.getInitialSelectionForRadio(deliveryOptions),
                invalidSelection: this.materialUtilities.getInvalidSelectionForRadio(deliveryOptions),
            }),
        );

        // load data back from the material model
        if (this.material.deliveryReturnSelectedOption) {
            this.deliveryReturnOptions.selectByPredicate(
                (entry) => entry.model.name === this.material.deliveryReturnSelectedOption,
            );
            if (this.material.deliveryReturnSelectedOption === ConfigConstants.returnCustomer) {
                this.customerAddress = _.cloneDeep(this.dopAddress);
            }
        }

        // prepare drop-off point list
        this.dropOffPointOptions = new DropdownModel('DropOffPointsOptions', cseDopList);
        modelOptionsLoader.load(this.dropOffPointOptions, 'MaterialReturn', 'DOPDropdown');

        // load data back from the material model
        if (this.material.dropOffPointSelectedOption) {
            this.dropOffPointOptions.selectByPredicate((entry) => {
                return entry.model.dropOffPoint.value === this.material.dropOffPointSelectedOption;
            });
        }

        this.materialReturnPartSet = <ArrayModel<MaterialPartSetModel>>(
            this.set(new ArrayModel<MaterialPartSetModel>('MatReturnPartSet'))
        );
        this.materialReturnPartSet.addModel(
            new MaterialPartSetModel(
                materialModel.awbReturn.value,
                materialModel.materialIdentifier.materialNumber.value,
                materialModel.materialIdentifier.orderNumber.value,
                materialModel.materialIdentifier.orderPosition.value,
                materialModel.quantity.value,
            ),
        );
        this.selectedPartSet = this.materialReturnPartSet.getModelByIndex(0);
        modelOptionsLoader.load(this.selectedPartSet.awb, 'MaterialReturn', 'AWB');
        this.selectedPartSet.quantity.options = {
            ...this.selectedPartSet.quantity.options,
            maxValue: this.selectedPartSet.quantity.value ? this.selectedPartSet.quantity.value : 0,
            readonly: true, // do n ot allow user to update the quantity as we cannot handle material split
        };
        // set model options for these fields in case of new material
        modelOptionsLoader.load(this.selectedPartSet.materialNumber, 'MaterialReturn', 'MaterialNumber');
        this.selectedPartSet.materialNumber.options = {
            ...this.selectedPartSet.materialNumber.options,
            applicationIdentifier: ConfigConstants.ApplicationIdentifier.ProductIdentifier,
        };
        modelOptionsLoader.load(this.selectedPartSet.orderNumber, 'MaterialReturn', 'OrderNumber');
        modelOptionsLoader.load(this.selectedPartSet.orderPosition, 'MaterialReturn', 'OrderPosition');

        // create new dop object or assign it from stored locally address and assign field validations
        this.dopAddress = this.material.dopAddress ? _.cloneDeep(this.material.dopAddress) : new CseDopModel();
        this.dopAddress.setFieldValidations('MaterialReturn');

        // the default customer address will be ndp header address set
        this.customerAddress = CseDopModel.fromAddressModel(materialModel.customerAddress);

        this.returnInfo = <TextModel>this.set(new TextModel('MatReturnText'));
        modelOptionsLoader.load(this.returnInfo, 'MaterialReturn', 'ReturnInfo');
        // load data back from the material model
        if (this.material.returnInfoText) {
            this.returnInfo.value = this.material.returnInfoText;
        }

        if (configService.getEnableAWBPackaging()) {
            this.awbPackaging = <TextModel>this.set(new TextModel('PkgAwbnr'));
            modelOptionsLoader.load(this.awbPackaging, 'MaterialReturn', 'PkgAwbnr');
            if (this.material.awbPackagingText) {
                this.awbPackaging.value = this.material.awbPackagingText;
            }
        }

        this.noPhysicalReturn = new DisplayDecorator<BooleanModel>(
            <string>translate.instant('MATERIAL_RETURN_NO_PHYSICAL_RETURN_LABEL'),
            new BooleanModel('MatIndicator'),
        );
        modelOptionsLoader.load(this.noPhysicalReturn, 'MaterialReturn', 'NoPhysicalReturn');
        // load data back from the material model
        if (this.material.noPhysicalReturn) {
            this.noPhysicalReturn.value = this.material.noPhysicalReturn;
        }
    }

    public getOutModel(): ODataMaterialCentralReturnOutModel {
        const outModel = <ODataMaterialCentralReturnOutModel>super.getOutModel();
        // MatIndicator values:
        // 'T' means bootstock return (user chose return action)
        // 'U' means bootstock scrap (user chose scrap action)
        // 'D' means material is deleted by NGM user (noPhysicalReturn checkbox)
        // 'R' for material return by NGM user (default)
        //
        // Only consider bootstock flags (isBootstockReturn, isBootStockScrapable) when the material
        // actually comes from bootstock source. This prevents notification materials from incorrectly
        // getting bootstock MatIndicator values when the modal is opened in non-bootstock context.
        outModel.MatIndicator =
            this.material.isBootstockMaterial && this.material.isBootstockReturn
                ? ConfigConstants.MAT_INDICATOR.BOOTSTOCK_RETURN
                : this.material.isBootstockMaterial && this.material.isBootStockScrapable
                  ? ConfigConstants.MAT_INDICATOR.BOOTSTOCK_SCRAP
                  : this.material.noPhysicalReturn
                    ? ConfigConstants.MAT_INDICATOR.DELETE
                    : ConfigConstants.MAT_INDICATOR.RETURN;
        outModel.DopAddressSet = this.dopAddress.getOutModel();
        if (this.deliveryReturnOptions.selectedEntry.model.name === ConfigConstants.returnTaxi) {
            outModel.Dopnr = this.KEY_TAXI;
        } else if (this.deliveryReturnOptions.selectedEntry.model.name === ConfigConstants.returnFedex) {
            outModel.Dopnr = this.KEY_FEDEX;
        } else if (this.deliveryReturnOptions.selectedEntry.model.name === ConfigConstants.returnDop) {
            outModel.Dopnr = this.dropOffPointOptions.selectedEntry.model.dropOffPoint.value;
            // SHCNGMP58-1348 Only for NGMP58, Region field should be empty if DopAddress is selected
            // Also for AA the Region needs to be empty
            outModel.DopAddressSet.Region = '';
        } else {
            outModel.Dopnr = '';
        }
        return outModel;
    }

    /**
     * Create an empty MaterialCentralReturnModel for ad hoc material return
     *
     * @param cseDopList
     */
    public static create(cseDopList: Array<DisplayDecorator<CseDopModel>>): MaterialCentralReturnModel {
        const injector = CustomInjector.getInjector();
        const userMasterService = injector.get(UserMasterService);
        const materialUtilities = injector.get(MaterialUtilitiesService);
        const userGidService = injector.get(UserGidService);
        const user = userMasterService.getSelectedUserModel();

        const material = new MaterialModel(undefined, undefined, '');
        material.notificationIdentifier = new NotificationIdentifierModel(userGidService.getUserGid(), '', '');
        material.customerAddress = AddressModel.build({ country: user.getCountry() });
        material.materialSource.sourceCode = MATERIAL_SOURCE_CODE.OTHER;
        material.statusString.value = 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_TOBERETURNED';
        material.materialStatus.statusCode = MATERIAL_STATUS_CODE.TOBERETURNED;
        material.activityDate.value = moment();

        return new MaterialCentralReturnModel(material, material.notificationIdentifier, cseDopList, materialUtilities);
    }

    public resetMaterialModelInfo(): MaterialModel {
        this.material.dopAddress = new CseDopModel();
        this.material.returnInfoText = '';
        this.material.awbPackagingText = '';
        this.material.deliveryReturnSelectedOption = '';
        this.material.noPhysicalReturn = false;
        this.material.dropOffPointSelectedOption = '';
        this.material.isRowSelected.value = false;
        return this.material;
    }
}

export interface ODataMaterialCentralReturnOutModel extends ODataNotificationIdentifierOutModel {
    Date: string;
    Time: string;
    MatReturnPartSet: ODataMaterialPartSetOutModel[];
    MatReturnText: string;
    MatIndicator: string;
    DopAddressSet: ODataCseDopOutModel;
    Dopnr: string;
}
